import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  qs,
  type ReviewDecideResponse,
  type ReviewDecision,
  type ReviewItemRow,
  type ReviewKind,
  type ReviewQueue,
  type ReviewStats,
  type ReviewSuggestion,
} from '../lib/api'
import { Empty, ErrorNote, Kbd } from '../components/Page'
import {
  CropPanel,
  SessionRail,
  Suggestions,
  TokenText,
  UpNextRail,
  itemLabel,
  markedWords,
} from '../components/review/Panels'
import { useSession } from '../lib/session'

/** The queue's kinds, in the order the board's tabs set them. */
const TABS: { kind: ReviewKind; label: string }[] = [
  { kind: 'low_confidence_ocr', label: 'Low confidence' },
  { kind: 'article_segmentation', label: 'Mis-split' },
  { kind: 'unmatched_advertiser', label: 'Advertiser' },
  { kind: 'ambiguous_byline', label: 'Byline' },
  { kind: 'routing_uncertain', label: 'Routing' },
]

const DECISIONS: { decision: ReviewDecision; label: string; key: string; tone?: 'primary' | 'mark' }[] = [
  { decision: 'accept', label: 'Accept', key: 'A', tone: 'primary' },
  { decision: 'correct', label: 'Correct', key: 'E' },
  { decision: 'skip', label: 'Skip', key: 'S' },
  { decision: 'illegible', label: 'Illegible', key: 'X', tone: 'mark' },
]

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)

/** The initials the review bar prints, from the display name or the address. */
function initials(name: string | undefined, email: string | undefined): string {
  const source = (name ?? email ?? '').trim()
  if (!source) return '??'
  const parts = source.split(/[\s.@_-]+/).filter(Boolean)
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase()
}

/**
 * The editor's review queue.
 *
 * High-volume repetitive work, built for a student worker with both hands on
 * the keyboard and no mouse: crop on the left, recognised text on the right,
 * one decision, next item. Every decision asks the API for the following item
 * in the same round trip, so the wait between two decisions is one request
 * rather than two.
 *
 * The queue is a first-class feature, not a fallback for a pipeline that
 * failed. Raw OCR stays authoritative: a correction writes `corrected_text`
 * and is attributed, and nothing an editor does here overwrites what the paper
 * printed.
 */
export function ReviewPage() {
  const queryClient = useQueryClient()
  const { me } = useSession()

  const [kind, setKind] = useState<ReviewKind>('low_confidence_ocr')
  const [items, setItems] = useState<ReviewItemRow[]>([])
  const [index, setIndex] = useState(0)
  const [selected, setSelected] = useState<number | null>(null)
  // 100 is the crop fitted to the well; the panel reports what that magnifies to.
  const [zoom, setZoom] = useState(100)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [last, setLast] = useState<{ text: string; at: number } | null>(null)
  const [decidedAt, setDecidedAt] = useState(Date.now())
  const [cursor, setCursor] = useState<string | null>(null)
  const editor = useRef<HTMLTextAreaElement>(null)
  const decided = useRef<string[]>([])
  const loadingMore = useRef(false)

  const queue = useQuery({
    queryKey: ['review', 'queue', kind],
    queryFn: () =>
      api.get<ReviewQueue>(`/review/queue${qs({ kind, status: 'open', limit: 50 })}`),
    retry: false,
  })

  const stats = useQuery({
    queryKey: ['review', 'stats'],
    queryFn: () => api.get<ReviewStats>('/review/stats'),
    retry: false,
  })

  // The queue seeds the local list; decisions extend it with the item the API
  // hands back, so the rail never empties under the editor's hands.
  useEffect(() => {
    if (!queue.data) return
    setItems(queue.data.items)
    setIndex(0)
    setCursor((queue.data as ReviewQueue & { cursor?: string | null }).cursor ?? null)
    decided.current = []
  }, [queue.data])

  /*
   * Paging is by cursor rather than offset, because the queue is being emptied
   * while it is read and an offset into a shrinking list silently skips items.
   * The next page is fetched before the editor reaches the end of this one, so
   * arrowing down the rail never stops at a wall.
   */
  useEffect(() => {
    if (!cursor || loadingMore.current) return
    if (index < items.length - 8) return
    loadingMore.current = true
    api
      .get<ReviewQueue & { cursor?: string | null }>(
        `/review/queue${qs({ kind, status: 'open', limit: 50, cursor })}`,
      )
      .then((page) => {
        setItems((current) => {
          const seen = new Set(current.map((entry) => entry.id))
          return [...current, ...page.items.filter((entry) => !seen.has(entry.id))]
        })
        setCursor(page.cursor ?? null)
      })
      .catch(() => setCursor(null))
      .finally(() => {
        loadingMore.current = false
      })
  }, [cursor, index, items.length, kind])

  const item = items[index]
  const total = queue.data?.total ?? items.length

  // Where the doubtful words sit in the text, and how many words there are to
  // doubt. Both come from the same walk, so the count in the panel head and
  // the marks in the prose can never disagree.
  const marked = useMemo(
    () => (item ? markedWords(item) : { pieces: [], marks: new Map() }),
    [item],
  )
  const positions = useMemo(
    () => [...marked.marks.keys()].sort((a, b) => a - b),
    [marked],
  )
  const wordCount = marked.pieces.filter((piece) => piece.position !== null).length
  const firstFlag = positions.length ? positions[0] : null

  // The first flagged token is selected on arrival: the editor's hands never
  // have to find it.
  useEffect(() => {
    setSelected(firstFlag)
    setEditing(false)
    setDraft('')
    setDecidedAt(Date.now())
  }, [item?.id, firstFlag])

  const selectedToken = selected === null ? null : (marked.marks.get(selected) ?? null)

  const decide = useMutation({
    mutationFn: (input: {
      id: string
      decision: ReviewDecision
      corrected_text?: string
      seconds: number
    }) =>
      api.post<ReviewDecideResponse>(`/review/${input.id}/decide`, {
        decision: input.decision,
        corrected_text: input.corrected_text,
        // How long this item took. The session's mean time and rate are
        // measured from these rather than estimated.
        seconds: input.seconds,
      }),
    onSuccess: (response, input) => {
      decided.current.push(input.id)
      setLast({
        text: `${input.decision}ed ${itemLabel(response.item)}`,
        at: Date.now(),
      })
      setItems((current) => {
        const seen = new Set(current.map((entry) => entry.id))
        if (response.next && !seen.has(response.next.id)) return [...current, response.next]
        return current
      })
      setIndex((current) => current + 1)
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['review', 'stats'] })
    },
  })

  const undo = useMutation({
    mutationFn: (id: string) => api.post<ReviewItemRow>(`/review/${id}/undo`),
    onSuccess: (restored) => {
      setItems((current) =>
        current.some((entry) => entry.id === restored.id)
          ? current.map((entry) => (entry.id === restored.id ? restored : entry))
          : [...current, restored],
      )
      setIndex((current) => Math.max(0, current - 1))
      setLast({ text: `undid ${itemLabel(restored)}`, at: Date.now() })
      void queryClient.invalidateQueries({ queryKey: ['review', 'stats'] })
    },
  })

  const send = useCallback(
    (decision: ReviewDecision, correctedText?: string) => {
      if (!item || decide.isPending) return
      // An empty correction is not a decision: it would erase the reading
      // without putting anything in its place, and the API refuses it.
      if (decision === 'correct' && !(correctedText ?? '').trim()) return
      decide.mutate({
        id: item.id,
        decision,
        corrected_text: correctedText,
        seconds: Math.round(((Date.now() - decidedAt) / 1000) * 10) / 10,
      })
    },
    [item, decide, decidedAt],
  )

  /**
   * Correcting opens the text for editing with the chosen suggestion applied.
   *
   * A one-word suggestion replaces the selected word; anything carrying a
   * space is a reading of the whole block (the raw option is exactly that) and
   * replaces all of it. Either way the editor sees the result before it is
   * saved, because a correction is a claim about what the paper printed.
   */
  const openCorrection = useCallback(
    (suggestion?: ReviewSuggestion) => {
      if (!item) return
      const base = item.corrected_text ?? item.raw_text ?? ''
      let next = base
      if (suggestion) {
        const singleWord = !/\s/.test(suggestion.text.trim())
        if (singleWord && selected !== null) {
          const pieces = base.split(/(\s+)/)
          next = pieces
            .map((piece, position) => (position === selected ? suggestion.text : piece))
            .join('')
        } else {
          next = suggestion.text
        }
      }
      setDraft(next)
      setEditing(true)
      window.setTimeout(() => editor.current?.focus(), 0)
    },
    [item, selected],
  )

  const moveFlag = useCallback(
    (step: number) => {
      if (!positions.length) return
      const at = positions.indexOf(selected ?? -1)
      const next = (at + step + positions.length) % positions.length
      setSelected(positions[next])
    },
    [positions, selected],
  )

  // The whole screen is one keyboard surface. A key pressed inside the
  // correction box belongs to the box, not to the queue.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        const lastId = decided.current.pop()
        if (lastId) undo.mutate(lastId)
        return
      }

      if (typing) {
        if (event.key === 'Escape') {
          event.preventDefault()
          setEditing(false)
        }
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault()
          send('correct', draft)
        }
        return
      }

      if (event.key === 'Tab') {
        event.preventDefault()
        moveFlag(event.shiftKey ? -1 : 1)
        return
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault()
        setIndex((current) => Math.min(items.length - 1, current + 1))
        return
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault()
        setIndex((current) => Math.max(0, current - 1))
        return
      }
      if (/^[1-9]$/.test(event.key)) {
        const position = Number(event.key) - 1
        const suggestions = item?.suggestions ?? []
        event.preventDefault()
        if (position < suggestions.length) openCorrection(suggestions[position])
        else if (position === suggestions.length) openCorrection()
        return
      }

      const letter = event.key.toLowerCase()
      if (letter === 'a') {
        event.preventDefault()
        send('accept')
      } else if (letter === 'e') {
        event.preventDefault()
        openCorrection(item?.suggestions.find((entry) => entry.default) ?? item?.suggestions[0])
      } else if (letter === 's') {
        event.preventDefault()
        send('skip')
      } else if (letter === 'x') {
        event.preventDefault()
        send('illegible')
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [send, openCorrection, moveFlag, items.length, item, draft, undo])

  const counts = queue.data?.counts ?? {}
  const elsewhere = TABS.filter((tab) => tab.kind !== kind && (counts[tab.kind] ?? 0) > 0)
  const today = stats.data?.today
  const progress = today && today.target ? Math.min(1, today.done / today.target) : 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The review bar: the queue's kinds, the day's count, who is deciding. */}
      <div className="flex h-[46px] flex-none items-center gap-3.5 border-b border-n300 px-[15px]">
        <div className="flex-none border-r border-n300 pr-3.5 font-mono text-[10px] uppercase tracking-[0.08em] text-ink2">
          Review
        </div>
        <div className="flex min-w-0 flex-none gap-[5px] overflow-x-auto">
          {TABS.map((tab) => {
            const on = tab.kind === kind
            const count = counts[tab.kind]
            return (
              <button
                key={tab.kind}
                type="button"
                onClick={() => setKind(tab.kind)}
                className="flex flex-none items-center gap-[7px] whitespace-nowrap rounded-[--radius-md] px-2.5 py-1 text-[13px]"
                style={{
                  background: on ? 'var(--jl-accent)' : 'transparent',
                  color: on ? 'var(--color-bg)' : 'var(--color-text)',
                  border: on ? '1px solid var(--jl-accent)' : '1px solid var(--color-neutral-300)',
                }}
              >
                {tab.label}
                <span
                  className="font-mono text-[10px]"
                  style={{ color: on ? 'rgba(243,242,242,0.75)' : 'var(--color-neutral-700)' }}
                >
                  {(count ?? 0).toLocaleString()}
                </span>
              </button>
            )
          })}
        </div>
        <div className="ml-auto flex flex-none items-center gap-3.5">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">Today</span>
            <span className="font-mono text-[14px]">{today?.done ?? 0}</span>
            <span className="font-mono text-[9.5px] text-ink2">/{today?.target ?? 0}</span>
          </div>
          <div
            className="flex h-[26px] w-[26px] items-center justify-center rounded-[--radius-md] border border-rule2 text-[12px] font-semibold text-ink2"
            title={me?.display_name ?? me?.email}
          >
            {initials(me?.display_name, me?.email)}
          </div>
        </div>
      </div>

      <div className="h-[6px] flex-none bg-n300" aria-hidden="true">
        <div className="h-full bg-accent" style={{ width: `${progress * 100}%` }} />
      </div>

      {queue.isError ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <Empty>
            The review API is not in this build yet. Stage{' '}
            <span className="font-mono text-[13px]">11_review</span> already fills{' '}
            <span className="font-mono text-[13px]">review_items</span> as each edition is indexed;
            this screen reads them from{' '}
            <span className="font-mono text-[13px]">/api/review/queue</span> and writes decisions to{' '}
            <span className="font-mono text-[13px]">/api/review/{'{id}'}/decide</span>. Until those
            routes exist there is nothing to work through here.
          </Empty>
          <ErrorNote error={queue.error} />
        </div>
      ) : queue.isLoading ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <Empty>Reading the queue.</Empty>
        </div>
      ) : !item ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <Empty>
            Nothing is waiting under this kind. The queue fills as editions are indexed: stage{' '}
            <span className="font-mono text-[13px]">11_review</span> raises an item for every block
            the recognition doubts, every article it could not split cleanly, and every advertiser it
            could not match.
          </Empty>
          {/* An empty screen names the first step, and here the first step is
              the kind that does have work waiting. */}
          {elsewhere.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 px-4">
              <span className="text-[15px] text-ink2">Work is waiting elsewhere:</span>
              {elsewhere.map((tab) => (
                <button
                  key={tab.kind}
                  type="button"
                  className="btn-secondary"
                  onClick={() => setKind(tab.kind)}
                >
                  {tab.label} · {counts[tab.kind]}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          <UpNextRail items={items} index={index} total={total} onPick={setIndex} />

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {/* What this item is, and where it sits in the queue. */}
            <div className="flex flex-none flex-wrap items-center gap-3.5 border-b border-n300 px-[18px] py-[11px]">
              <span
                className="rounded-[--radius-md] px-[7px] py-[2px] font-mono text-[10px] uppercase tracking-[0.06em]"
                style={{ background: 'var(--color-accent-2-700)', color: 'var(--color-bg)' }}
              >
                {item.kind}
              </span>
              <span className="whitespace-nowrap font-mono text-[11.5px]">
                {item.pub_date ?? 'undated'} · {itemLabel(item)}
                {item.reading_order !== null && item.reading_order !== undefined
                  ? ` · ord ${String(item.reading_order).padStart(2, '0')}`
                  : ''}
              </span>
              <span className="whitespace-nowrap font-mono text-[11.5px] text-mark">
                {item.confidence === null || item.confidence === undefined
                  ? 'no score'
                  : `${(item.confidence * 100).toFixed(1)}%`}
              </span>
              <span className="ml-auto whitespace-nowrap font-mono text-[10px] text-ink2">
                <span className="uppercase">
                  Item {(index + 1).toLocaleString()} of {total.toLocaleString()}
                </span>{' '}
                · {item.engine ?? 'engine not recorded'}
              </span>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
              <CropPanel item={item} zoom={zoom} onZoom={setZoom} />

              <div className="flex min-h-0 flex-col">
                <div className="flex flex-none items-center gap-2.5 border-b border-n300 px-[18px] pb-[9px] pt-[11px]">
                  <div className="chrome text-[11px]">Recognised text</div>
                  <div className="ml-auto font-mono text-[10px] uppercase text-ink2">
                    {positions.length} flagged of {wordCount} tokens
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-4">
                  {editing ? (
                    <div>
                      <label className="label" htmlFor="correction">
                        The correction, which lands in corrected_text
                      </label>
                      <textarea
                        id="correction"
                        ref={editor}
                        className="input min-h-[150px] w-full text-[16.5px] leading-[1.7]"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={decide.isPending || !draft.trim()}
                          onClick={() => send('correct', draft)}
                        >
                          Save the correction
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setEditing(false)}
                        >
                          Cancel
                        </button>
                        <span className="ml-auto font-mono text-[10px] uppercase text-ink2">
                          {MAC ? 'Cmd' : 'Ctrl'} Enter saves · Esc cancels
                        </span>
                      </div>
                      <p className="mt-2.5 font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
                        raw_text is not touched. The correction is attributed to you and the unit is
                        re-indexed.
                      </p>
                    </div>
                  ) : (
                    <TokenText item={item} selected={selected} onSelect={setSelected} />
                  )}

                  {!editing && item.suggestions.length > 0 && (
                    <div className="mt-[18px] border-t border-n300 pt-3.5">
                      <div className="mb-2.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink2">
                        Suggestions for the selected token
                        {selectedToken ? ` · “${selectedToken.text}”` : ''}
                      </div>
                      <Suggestions
                        suggestions={item.suggestions}
                        onPick={(suggestion) => openCorrection(suggestion)}
                        onFreeform={() => openCorrection()}
                      />
                    </div>
                  )}

                  {!editing && item.context && (
                    <div className="mt-4 border-t border-n300 pt-3.5">
                      <div className="mb-2.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink2">
                        Context from the same edition
                      </div>
                      <p className="text-[14.5px] leading-[1.5] text-ink2">{item.context}</p>
                    </div>
                  )}
                </div>

                {/* One decision, then the next item. */}
                <div className="flex-none border-t border-n300 bg-surface px-[18px] py-[11px]">
                  <ErrorNote error={decide.error ?? undo.error} />
                  <div className="mb-2.5 flex gap-1.5">
                    {DECISIONS.map((entry) => (
                      <button
                        key={entry.decision}
                        type="button"
                        disabled={decide.isPending}
                        onClick={() =>
                          entry.decision === 'correct'
                            ? openCorrection(
                                item.suggestions.find((one) => one.default) ?? item.suggestions[0],
                              )
                            : send(entry.decision)
                        }
                        className={`flex h-[42px] items-center justify-center gap-[9px] rounded-[--radius-md] text-[15px] disabled:opacity-45 ${
                          entry.tone === 'mark' ? 'px-3.5' : 'flex-1'
                        }`}
                        style={
                          entry.tone === 'primary'
                            ? {
                                background: 'var(--jl-accent)',
                                color: 'var(--color-bg)',
                                fontWeight: 600,
                              }
                            : entry.tone === 'mark'
                              ? {
                                  border: '1px solid var(--color-neutral-400)',
                                  color: 'var(--color-accent-2-700)',
                                }
                              : { border: '1px solid var(--color-neutral-400)' }
                        }
                      >
                        {entry.label}
                        <span
                          className="font-mono text-[11px]"
                          style={{
                            opacity: entry.tone === 'primary' ? 0.75 : 1,
                            color:
                              entry.tone === 'primary'
                                ? 'inherit'
                                : entry.tone === 'mark'
                                  ? 'inherit'
                                  : 'var(--color-neutral-700)',
                          }}
                        >
                          {entry.key}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase text-ink2">
                    <div className="flex items-center gap-1.5">
                      <Kbd>Tab</Kbd> next flag
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Kbd>1</Kbd>
                      <span>to</span>
                      <Kbd>5</Kbd> pick a suggestion
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Kbd>{MAC ? 'Cmd' : 'Ctrl'}</Kbd>
                      <Kbd>Z</Kbd> undo last decision
                    </div>
                    <div className="ml-auto text-ink">
                      {last
                        ? `Last: ${last.text} · ${((Date.now() - last.at) / 1000).toFixed(1)} s ago`
                        : `This item opened ${((Date.now() - decidedAt) / 1000).toFixed(0)} s ago`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <SessionRail stats={stats.data} />
        </div>
      )}
    </div>
  )
}
